import React, { Fragment, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { graphql, useQuery } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import qs from 'query-string';

import WaldogramConfigurator from '../../../../components/global/WaldogramConfigurator';
import Button from '../../../../components/global/Button';
import { AlbumForWaldogramConfiguration } from '../../graphql/queries/waldograms';
import useWaldogramConfig from '../../../../components/global/WaldogramConfigurator/hooks/useWaldogramConfig';
import { AccountForWebGalleries } from '../../graphql/queries/account';
import { CURRENCY } from '../../../../constants/currency';
import { formatPriceForDisplay } from '../../../../util/format-money';
import {
  getCustomPrintPriceFromTotalPrice,
  getWaldogramPrintType,
} from '../../utilities/waldograms';
import { checkout } from '../../../../graphql/mutations/checkout';
import { checkoutPrice } from '../../../../graphql/queries/checkout';
import Alert from '../../../../components/global/Alert';
import useWaldogramCheckout from '../../hooks/useWaldogramCheckout';
import WaldogramSuccessModalContainer from './WaldogramSuccessModalContainer';
import Spinner from '../../../../components/global/Spinner';
import { WALDOGRAM_PRINT_TYPE } from '../../constants/web-gallery-waldogram-constants';
import {
  trackWebGalleryEvent,
  ANALYTIC_EVENTS,
} from '../../utilities/analytics';
import { IDENTITY_RELATIONSHIP } from '../../../../constants/identity';
import { photoUploadSuccess } from '../../../../actions/photo-upload';
import { verifyWaldogramConfig } from '../../../../components/global/WaldogramConfigurator/helpers';
import { CustomPrintConfigurationPhotos } from '../../../../graphql/queries/custom-prints';
import { createCustomPrintCandidatePhotoGroups } from '../../../../components/global/CustomPrintConfigurator/helpers';
import { PhotoForStandaloneView } from '../../../../graphql/queries/photos';
import useComponentSize from '../../../../hooks/useComponentSize';
import { getAutoFitPositionPhotoCropbox } from '../../../../services/cards/components/CardElementPhoto/helpers';
import { getMaxThumb } from '../../../../util/photo';
import { STRIPE_CHECKOUT_MODAL } from '../../../../components/global/StripeCheckout';
import useOpenModal from '../../../../hooks/useOpenModal';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../constants/account-referrals';

const propTypes = {
  loading: PropTypes.bool,
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  waldogram: PropTypes.shape({
    id: PropTypes.string,
  }),
  loadingPrice: PropTypes.bool,
  price: PropTypes.shape({
    total_price: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.oneOf(Object.values(CURRENCY)),
    }),
    total_shipping_cost: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.oneOf(Object.values(CURRENCY)),
    }),
    total_tax: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.oneOf(Object.values(CURRENCY)),
    }),
  }),
  customPrintCheckout: PropTypes.func.isRequired,
  candidatePhotoGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  ),
  onGoBack: PropTypes.func,
  goBackLabel: PropTypes.string,
  historyPath: PropTypes.string,
  photoId: PropTypes.string,
};

const WaldogramConfiguratorContainer = ({
  loading,
  waldogram,
  price,
  loadingPrice,
  account,
  customPrintCheckout,
  candidatePhotoGroups,
  onGoBack,
  goBackLabel,
  historyPath,
  photoId: photoIdProp,
}) => {
  const openModal = useOpenModal();
  const location = useLocation();
  const mainSlotRef = useRef();
  const waldogramRef = useRef();
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  // Use this instead of the query loading state since we want to present
  // the loading state until we actually set the photo inside the frame.
  const [queryPhotoLoaded, setQueryPhotoLoaded] = useState(false);

  const { photoId: queryPhotoId } = qs.parse(location.search);
  // Prefer an explicitly passed photoId (e.g. from the purchasing options modal)
  // over the query-string one so we can pre-load the photo in both cases.
  const photoId = photoIdProp ?? queryPhotoId;

  const waldogramSize = useComponentSize(waldogramRef);
  const mainSlotSize = useComponentSize(mainSlotRef);

  const {
    config,
    setPhoto,
    setPhotoCropbox,
    setCaption,
    setCabinName,
    setIdentityName,
    setEmoji,
    resetWaldogram,
  } = useWaldogramConfig();

  // Fetch photo if photoId is provided
  const { data: { photo: queryPhoto } = {} } = useQuery(
    PhotoForStandaloneView,
    {
      variables: { id: photoId },
      skip: !photoId,
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    if (photoId && queryPhoto?.id && !queryPhotoLoaded) {
      setPhoto(0, queryPhoto);
      setQueryPhotoLoaded(true);
    }
  }, [queryPhoto, photoId, queryPhotoLoaded]);

  useEffect(() => {
    if (
      photoId &&
      queryPhotoLoaded &&
      waldogramSize?.width &&
      mainSlotSize?.width
    ) {
      const { width, height } = waldogramSize;
      setPhotoCropbox(
        0,
        getAutoFitPositionPhotoCropbox({
          cardElement: {
            frame: {
              width: mainSlotSize.width / waldogramSize.width,
              height: mainSlotSize.height / waldogramSize.height,
            },
          },
          cardWidth: width,
          cardHeight: height,
          thumb: getMaxThumb(queryPhoto?.thumbnail_urls),
        }),
      );
    }
  }, [waldogramSize?.width, mainSlotSize?.width, photoId, queryPhotoLoaded]);

  const {
    onCheckout,
    checkingOut,
    checkoutError,
    checkoutSuccess,
    setCheckoutError,
    setCheckingOut,
    setCheckoutSuccess,
  } = useWaldogramCheckout({
    checkoutMutation: customPrintCheckout,
  });

  const printType = getWaldogramPrintType(waldogram);

  useEffect(() => {
    if (checkoutSuccess) {
      trackWebGalleryEvent(ANALYTIC_EVENTS.POST_CARD_CREATED, {
        message_type: config.caption.length,
        recipient_type:
          printType === WALDOGRAM_PRINT_TYPE.ONSITE ? 'onsite' : 'offsite',
        fullfillment:
          printType === WALDOGRAM_PRINT_TYPE.ONSITE ? 'Onsite' : 'Vendor',
      });
    }
  }, [checkoutSuccess]);

  const pricing = getCustomPrintPriceFromTotalPrice(price);
  const totalPrice = formatPriceForDisplay(pricing.total);
  const shippingPrice = formatPriceForDisplay(pricing.shipping);

  const onPhotoSelect = ({ position, photo = null }) => {
    if (position == null) {
      setSelectedPhoto(null);
      return;
    }

    setSelectedPhoto({
      position,
      photo,
    });
  };

  const onStripeTokenReceived = (
    stripeToken,
    stripePaymentMethodId,
    stripeConnectAccountId,
  ) => {
    onCheckout({
      stripeToken,
      account,
      waldogram,
      config,
      stripePaymentMethodId,
      stripeConnectAccountId,
    });
  };

  if (photoId && !queryPhotoLoaded) {
    return (
      <div className="wg-waldogram-content-wrap-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <Fragment>
      <div ref={waldogramRef} className="wg-waldogram-content">
        {loading && (
          <div className="text-center my-4">
            <Spinner size="lg" />
            <span className="mt-1 text-sm d-block">
              One second while your postcard is being baked!
            </span>
          </div>
        )}

        <WaldogramConfigurator
          selectedPhoto={selectedPhoto}
          printType={printType}
          config={config}
          onPhotoSelect={onPhotoSelect}
          onPhotoCropboxSet={setPhotoCropbox}
          onPhotoSet={photo => {
            setPhoto(selectedPhoto.position, photo);
            if (selectedPhoto.position === 0 && photo && !photo?.custom) {
              setPhotoCropbox(
                0,
                getAutoFitPositionPhotoCropbox({
                  cardElement: {
                    frame: {
                      width: mainSlotSize.width / waldogramSize.width,
                      height: mainSlotSize.height / waldogramSize.height,
                    },
                  },
                  cardWidth: waldogramSize.width,
                  cardHeight: waldogramSize.height,
                  thumb: getMaxThumb(photo?.thumbnail_urls),
                }),
              );
            }
            setSelectedPhoto(null);
          }}
          onCaptionSet={setCaption}
          onCabinNameSet={setCabinName}
          onIdentityNameSet={setIdentityName}
          onEmojiSet={setEmoji}
          candidatePhotoGroups={candidatePhotoGroups}
          mainSlotRef={mainSlotRef}
        />
      </div>

      <div className="wg-waldogram-sidebar">
        <p className="text-extra-bold">What are Waldograms?</p>

        <div className="text-sm">
          {printType === WALDOGRAM_PRINT_TYPE.ONSITE ? (
            <Fragment>
              <p>
                A beautiful and fun customized postcard you can create and send
                to your camper – hand delivered!
              </p>
              <p>
                <strong>Size</strong>: 8.5&times;5.5
              </p>
            </Fragment>
          ) : (
            <Fragment>
              <p>
                A beautiful and fun customized post card you can create and send
                to your camper – delivered via USPS within 4 to 6 business days!
              </p>
              <p>
                <strong>Size</strong>: 4&times;6
              </p>
            </Fragment>
          )}
        </div>

        {checkoutError.length > 0 && (
          <Alert type="danger">
            {Array.isArray(checkoutError) ? (
              <Fragment>
                <p className="text-extra-bold mb-1">
                  The following errors were found:
                </p>
                <ul className="m-0 pr-1">
                  {checkoutError.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </Fragment>
            ) : (
              checkoutError
            )}
          </Alert>
        )}

        <Button
          btnBlock
          className="wg-waldogram-checkout-btn mt-4"
          disabled={loadingPrice || !price || checkingOut}
          onClick={() => {
            const configErrors = verifyWaldogramConfig(config);

            if (configErrors.length > 0) {
              setCheckoutError(configErrors);
              setCheckingOut(false);
              return;
            }

            openModal(STRIPE_CHECKOUT_MODAL, {
              cardRequired: false,
              title: <h3 className="text-center">Waldogram Purchase</h3>,
              buttonLabel: `Pay $${totalPrice}`,
              onSuccess: token => onStripeTokenReceived(token, null, null),
              checkoutPriceVariables: ({ promo_code: promoCode }) => ({
                account_id: account.id,
                promo_code: promoCode,
                custom_print_items: [
                  {
                    album_custom_print_id: waldogram.id,
                    identity_id: account.linked_identities.records.find(
                      identity =>
                        identity.relationship === IDENTITY_RELATIONSHIP.PRIMARY,
                    ).id,
                    quantity: 1,
                    include_shipping: true,
                  },
                ],
              }),
              defaultName: account?.name ?? '',
              defaultEmail: account?.email_address ?? '',
              accountReferralTypes:
                pricing?.total > 0
                  ? [
                      ACCOUNT_REFERRAL_TYPE.WALDOGRAM_PROMO,
                      ACCOUNT_REFERRAL_TYPE.ONE_FREE_WALDOGRAM,
                      ACCOUNT_REFERRAL_TYPE.TIER_1_WALDOGRAM_PROMO,
                    ]
                  : undefined,
            });
          }}
        >
          {checkingOut ? (
            <Spinner className="spinner-white" />
          ) : (
            <Fragment>
              <span>
                Send Card {price && <Fragment>&ndash; ${totalPrice}</Fragment>}
              </span>

              {!!pricing.shipping && (
                <span className="wg-waldogram-checkout-btn-shipping">
                  Includes ${shippingPrice} shipping costs
                </span>
              )}
            </Fragment>
          )}
        </Button>
      </div>

      <WaldogramSuccessModalContainer
        forceOpen={checkoutSuccess}
        onReset={() => resetWaldogram()}
        onClose={() => setCheckoutSuccess(false)}
        onGoBack={onGoBack}
        goBackLabel={goBackLabel}
        historyPath={historyPath}
      />
    </Fragment>
  );
};

WaldogramConfiguratorContainer.propTypes = propTypes;

const mapStateToProps = ({ auth, photoUploads }) => ({
  authenticated: auth.authenticated,
  uploadedPhoto: photoUploads.photo,
});

const mapDispatchToProps = {
  photoUploadSuccessAction: photoUploadSuccess,
};

const withAccount = graphql(AccountForWebGalleries, {
  props: ({ data }) => ({
    account: data?.me,
  }),
  skip: ({ authenticated }) => !authenticated,
});

const withAlbumForWaldogramConfig = graphql(AlbumForWaldogramConfiguration, {
  options: ({ match, albumId }) => ({
    variables: {
      id: albumId ?? match?.params?.albumId ?? match?.params?.hierarchyItemId,
    },
    fetchPolicy: 'cache-and-network',
  }),
  props: ({ data: { loading, album } = {}, ownProps: { albumId } }) => ({
    loading,
    waldogram: album?.custom_prints?.records?.[0],
    albumId: albumId ?? album?.id,
  }),
  skip: ({ match, albumId }) =>
    !albumId && !match?.params?.albumId && !match?.params?.hierarchyItemId,
});

const withPurchasedCustomPrintCandidatePhotos = graphql(
  CustomPrintConfigurationPhotos,
  {
    options: ({ account, waldogram }) => ({
      variables: {
        albumCustomPrintId: waldogram.id,
        identityId: account.linked_identities.records[0].id,
      },
    }),
    props: ({ data: { customPrintConfigurationPhotos, fetchMore } = {} }) => {
      return {
        candidatePhotoGroups: createCustomPrintCandidatePhotoGroups({
          source: customPrintConfigurationPhotos,
          mappings: [
            {
              id: 'matches',
              label: 'Matches',
              path: 'matched_photos_extended',
              mapper: record => record.photo,
            },
            {
              id: 'favorites',
              label: 'Favorites',
              path: 'favorited_photos',
            },
            {
              id: 'allPhotos',
              label: 'All Photos',
              path: 'all_photos',
            },
          ],
          fetchMore,
        }),
      };
    },
    skip: ({ account, waldogram }) => !account || !waldogram,
  },
);

const withCheckoutPrice = graphql(checkoutPrice, {
  options: ({ account, waldogram }) => ({
    variables: {
      account_id: account.id,
      custom_print_items: [
        {
          album_custom_print_id: waldogram.id,
          identity_id: account.linked_identities.records.find(
            identity => identity.relationship === IDENTITY_RELATIONSHIP.PRIMARY,
          ).id,
          quantity: 1,
          include_shipping: true,
        },
      ],
    },
  }),
  props: ({ data: { loading, checkoutPrice: response } }) => ({
    price: response,
    loadingPrice: loading,
  }),
  skip: ({ account, waldogram }) => !account || !waldogram,
});

const withCheckout = graphql(checkout, {
  props: ({ mutate }) => ({
    customPrintCheckout: ({
      accountId,
      email,
      stripeTokenId,
      customPrintItems,
      stripePaymentMethodId,
      stripeConnectAccountId,
    }) =>
      mutate({
        variables: {
          input: {
            account_id: accountId,
            email_address: email,
            stripe_token: stripeTokenId,
            custom_print_items: customPrintItems,
            platform: 'WEB',
            stripe_payment_method_id: stripePaymentMethodId,
            stripe_connect_account_id: stripeConnectAccountId,
          },
        },
      }),
  }),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withRouter,
  withAccount,
  withAlbumForWaldogramConfig,
  withPurchasedCustomPrintCandidatePhotos,
  withCheckoutPrice,
  withCheckout,
)(WaldogramConfiguratorContainer);
